import type { PriorityAssessment } from '../lib/priorityAssessment'

export const TICKET_STATUSES = ['New', 'Assigned', 'InProgress', 'OnHold', 'Deferred', 'Resolved', 'Closed'] as const
export type TicketStatus = (typeof TICKET_STATUSES)[number]

export const TICKET_PRIORITIES = ['Low', 'Medium', 'High', 'Critical'] as const
export type TicketPriority = (typeof TICKET_PRIORITIES)[number]
// Categories are admin-managed in it_ticket_categories (00574) and read via
// useTicketCategories(). This list is only the FALLBACK used when that table
// can't be read (e.g. before the migration is applied), so filing a ticket
// never breaks. It matches the migration's seed.
export const DEFAULT_TICKET_CATEGORIES = [
  'Training', 'Question', 'Process', 'Data', 'Security', 'Reporting', 'Software',
] as const

// A support customer tickets are filed under — see 00563_it_ticketing_customers.sql.
// Distinct on purpose from PPM's `organizations` portfolio hierarchy: see
// lib/ticketingCustomerContext.ts for why the two pickers are kept separate.
export interface TicketingCustomer {
  id: string
  name: string
  active: boolean
}

export interface Ticket {
  id: string
  orgId: string | null
  ticketNumber: string
  title: string
  description: string
  status: TicketStatus
  priority: TicketPriority
  category: string
  dueDate: string | null
  resolvedAt: string | null
  // "Created for": who the ticket is about (requester_id, 00550).
  requesterId: string
  // "Created by": who entered it, stamped server-side (00570). Null where not recorded.
  createdBy: string | null
  assigneeId: string | null
  attrs: Record<string, unknown>
  // Priority questionnaire (00571); null if the ticket was never assessed.
  priorityAssessment: PriorityAssessment | null
  createdAt: string
  updatedAt: string
}

// Admin-defined custom field definitions — see 00558_it_ticketing_field_defs.sql.
// Values live in Ticket.attrs, keyed by `key`. Mirrors CRM's CrmFieldDef exactly.
export const TICKET_FIELD_TYPES = ['text', 'textarea', 'number', 'date', 'select', 'multiselect', 'checkbox'] as const
export type TicketFieldType = (typeof TICKET_FIELD_TYPES)[number]

export interface TicketFieldDef {
  id: string
  orgId: string | null
  key: string
  label: string
  fieldType: TicketFieldType
  options: string[]
  required: boolean
  section: string | null
  seq: number
  helpText: string | null
  active: boolean
  createdBy: string | null
  createdAt: string
  updatedAt: string
}

export interface TicketFieldDefInput {
  key: string
  label: string
  fieldType: TicketFieldType
  options?: string[]
  required?: boolean
  section?: string | null
  seq?: number
  helpText?: string | null
}

export interface TicketAttachment {
  id: string
  orgId: string | null
  ticketId: string
  fileName: string
  mimeType: string | null
  sizeBytes: number | null
  storagePath: string
  uploadedBy: string | null
  createdAt: string
}

export interface TicketAuditEntry {
  id: string
  action: string
  summary: string
  actor: string | null
  actorName: string | null
  // The ticket id; set by listAllTicketAudit (the per-ticket read already knows it).
  entityId?: string
  payload: Record<string, unknown>
  createdAt: string
}

export interface TicketComment {
  id: string
  orgId: string | null
  ticketId: string
  authorId: string
  body: string
  createdAt: string
}

// One row per (status, assignee) change — see 00553_it_ticketing_state_history.sql.
// `startedAt` is when this combination began; its end is the next row's
// `startedAt` (or now()/ticket.resolvedAt for the last row).
export interface TicketStateEvent {
  id: string
  ticketId: string
  // The customer the ticket was under during this stretch (00553; also on customer change since 00575).
  orgId: string | null
  status: TicketStatus
  assigneeId: string | null
  changedBy: string | null
  startedAt: string
}

// A computed span: time spent in one status, or in one assignee's queue,
// derived client-side from consecutive TicketStateEvent rows.
export interface TicketDurationSpan {
  ticketId: string
  value: string // status name, or assigneeId (or 'unassigned' / 'deferred')
  startedAt: string
  endedAt: string
  durationMs: number
}

export interface TicketInput {
  title: string
  description: string
  category: string
  priority?: TicketPriority
  status?: TicketStatus
  requesterId: string
  assigneeId?: string | null
  dueDate?: string | null
  attrs?: Record<string, unknown>
  priorityAssessment?: PriorityAssessment | null
}

/**
 * A new ticket can name its customer (org_id) explicitly: the New ticket form
 * does when the person can file for more than one customer, e.g. an admin
 * viewing "All customers". Without it, the active customer is used. Only
 * creation takes this; updates never move a ticket between customers.
 */
export interface NewTicketInput extends TicketInput {
  orgId?: string | null
}

// RBAC + workflow (00559) — ported from the original's permissions.js /
// WorkflowRule / TicketApproval / Delegation models.
export const TICKETING_ROLES = ['admin', 'agent', 'requester'] as const
export type TicketingRole = (typeof TICKETING_ROLES)[number]

export interface TicketingPermissionDef {
  key: string
  label: string
  category: 'Tickets' | 'Reports & History' | 'Workflow' | 'Admin'
}

// Same 18 keys as the original's backend/src/permissions.js PERMISSIONS catalog.
export const TICKETING_PERMISSIONS: TicketingPermissionDef[] = [
  { key: 'view_tickets', label: 'View tickets', category: 'Tickets' },
  { key: 'create_tickets', label: 'Create tickets', category: 'Tickets' },
  { key: 'edit_tickets', label: 'Edit ticket details', category: 'Tickets' },
  { key: 'assign_tickets', label: 'Assign tickets to agents', category: 'Tickets' },
  { key: 'close_tickets', label: 'Resolve or close tickets', category: 'Tickets' },
  { key: 'delete_tickets', label: 'Delete tickets', category: 'Tickets' },
  { key: 'bulk_edit_tickets', label: 'Bulk edit tickets', category: 'Tickets' },
  { key: 'import_export_tickets', label: 'Import and export tickets', category: 'Tickets' },
  { key: 'view_reports', label: 'View reports', category: 'Reports & History' },
  { key: 'manage_custom_report_cards', label: 'Create, edit, or delete custom report cards', category: 'Reports & History' },
  { key: 'view_history', label: 'View audit history', category: 'Reports & History' },
  { key: 'manage_workflow_rules', label: 'Manage workflow rules', category: 'Workflow' },
  { key: 'manage_delegations', label: "Set up a delegation on someone else's behalf", category: 'Workflow' },
  { key: 'manage_users', label: 'Manage users', category: 'Admin' },
  { key: 'manage_categories', label: 'Manage categories', category: 'Admin' },
  { key: 'manage_fields', label: 'Manage custom fields', category: 'Admin' },
  { key: 'manage_ticket_numbering', label: 'Manage ticket numbering settings', category: 'Admin' },
  { key: 'manage_roles', label: 'Manage roles & permissions', category: 'Admin' },
]

/**
 * Permissions that no longer grant anything: since 00589 every settings
 * change is decided by the ticketing admin list (it_ticketing_is_admin()),
 * so these are hidden from Role Permissions rather than left as dead boxes.
 */
export const ADMIN_LIST_PERMISSIONS: ReadonlySet<string> = new Set([
  'manage_workflow_rules',
  'manage_delegations',
  'manage_users',
  'manage_categories',
  'manage_fields',
  'manage_ticket_numbering',
  'manage_roles',
])

export interface TicketRolePermission {
  id: string
  orgId: string | null
  role: TicketingRole
  permission: string
  granted: boolean
}

export type ApproverType = 'user' | 'role'

export interface TicketWorkflowRule {
  id: string
  orgId: string | null
  name: string
  fieldKey: string
  value: string
  approverType: ApproverType
  approverUserId: string | null
  approverRole: TicketingRole | null
  active: boolean
  createdBy: string | null
  createdAt: string
  updatedAt: string
}

export interface TicketWorkflowRuleInput {
  name: string
  fieldKey: string
  value: string
  approverType: ApproverType
  approverUserId?: string | null
  approverRole?: TicketingRole | null
  active?: boolean
}

export const APPROVAL_STATUSES = ['pending', 'approved', 'rejected'] as const
export type ApprovalStatus = (typeof APPROVAL_STATUSES)[number]

export interface TicketApproval {
  id: string
  orgId: string | null
  ticketId: string
  ruleId: string
  status: ApprovalStatus
  comment: string | null
  decidedAt: string | null
  decidedBy: string | null
  createdAt: string
}

export interface TicketDelegation {
  id: string
  orgId: string | null
  fromUserId: string
  toUserId: string
  endsAt: string | null
  active: boolean
  createdBy: string | null
  createdAt: string
  updatedAt: string
}

export interface TicketDelegationInput {
  fromUserId: string
  toUserId: string
  endsAt?: string | null
  active?: boolean
}

// Report cards + saved views (00561) — ported from the original's
// CustomReportCard model and Saved Views menu.
export const REPORT_CARD_KINDS = ['kpi', 'breakdown'] as const
export type ReportCardKind = (typeof REPORT_CARD_KINDS)[number]

export const REPORT_CARD_MEASURES = ['count', 'avg_duration'] as const
export type ReportCardMeasure = (typeof REPORT_CARD_MEASURES)[number]

export const REPORT_CHART_TYPES = ['bar', 'column', 'line', 'pie', 'table'] as const
export type ReportChartType = (typeof REPORT_CHART_TYPES)[number]

// 'bar' is horizontal bars and 'column' vertical, matching the shared
// ReportChart's own kinds (00587 widened the DB check to these five).
export const REPORT_CHART_TYPE_LABEL: Record<ReportChartType, string> = {
  bar: 'Bar', column: 'Column', line: 'Line', pie: 'Pie', table: 'Table',
}

// Fixed groupable dimensions, matching the original's groupableFields() —
// free-text/number/date fields are excluded (no sensible finite grouping).
export const GROUPABLE_FIELDS = ['status', 'priority', 'category', 'assignee', 'requester', 'createdBy'] as const
export type GroupableField = (typeof GROUPABLE_FIELDS)[number]

// What a report card can group (and filter) by: a ticket field above, the
// ticket's approval status, or an admin-defined custom field as
// "custom:<field key>". Only select, multi-select and checkbox custom fields
// group sensibly (a fixed set of values); text, number and date don't.
export const APPROVAL_STATUS_GROUP = 'approvalStatus'
export type ReportGroupKey = GroupableField | typeof APPROVAL_STATUS_GROUP | `custom:${string}`
export const GROUPABLE_CUSTOM_FIELD_TYPES: readonly TicketFieldType[] = ['select', 'multiselect', 'checkbox']

export function isReportGroupKey(v: unknown): v is ReportGroupKey {
  return typeof v === 'string'
    && ((GROUPABLE_FIELDS as readonly string[]).includes(v) || v === APPROVAL_STATUS_GROUP || /^custom:[A-Za-z0-9_]+$/.test(v))
}

export interface TicketReportCard {
  id: string
  orgId: string | null
  title: string
  kind: ReportCardKind
  groupByField: ReportGroupKey | null
  measure: ReportCardMeasure
  chartType: ReportChartType | null
  filterField: ReportGroupKey | null
  filterValue: string | null
  createdBy: string | null
  createdAt: string
  updatedAt: string
}

export interface TicketReportCardInput {
  title: string
  kind: ReportCardKind
  groupByField?: ReportGroupKey | null
  measure?: ReportCardMeasure
  chartType?: ReportChartType | null
  filterField?: ReportGroupKey | null
  filterValue?: string | null
}

// Each column filter is a comma-separated list of values (lib/gridFilterParams.ts);
// views saved before column filters hold a single value, which reads the same.
export interface TicketSavedViewFilters {
  status?: string
  priority?: string
  category?: string
  assignee?: string
  requester?: string
  createdBy?: string
  tasks?: string
  q?: string
}

export interface TicketSavedView {
  id: string
  orgId: string | null
  ownerId: string | null
  name: string
  filters: TicketSavedViewFilters
  shared: boolean
  createdAt: string
}

export interface TicketSavedViewInput {
  name: string
  filters: TicketSavedViewFilters
  shared?: boolean
}

export const STATUS_CLASS: Record<TicketStatus, string> = {
  New: 'bg-blue-50 text-blue-700',
  Assigned: 'bg-indigo-50 text-indigo-700',
  InProgress: 'bg-amber-50 text-amber-700',
  OnHold: 'bg-gray-100 text-gray-600',
  Deferred: 'bg-violet-50 text-violet-700',
  Resolved: 'bg-emerald-50 text-emerald-700',
  Closed: 'bg-gray-200 text-gray-500',
}

export const PRIORITY_CLASS: Record<TicketPriority, string> = {
  Low: 'bg-gray-100 text-gray-600',
  Medium: 'bg-blue-50 text-blue-700',
  High: 'bg-orange-50 text-orange-700',
  Critical: 'bg-red-50 text-red-700',
}


// Admin-managed ticket category (00574). Tickets store the category NAME as
// text; a retired (inactive) category stays on old tickets but isn't offered
// for new ones.
export interface TicketCategory {
  id: string
  name: string
  active: boolean
  seq: number
}

/** The canonical spelling of `raw` among `names` (case-insensitive, trimmed), or null if it isn't one. */
export function normalizeTicketCategory(raw: string, names: readonly string[] = DEFAULT_TICKET_CATEGORIES): string | null {
  const q = raw.trim().toLowerCase()
  return names.find((c) => c.toLowerCase() === q) ?? null
}

// A checklist item on one ticket — see 00569_it_ticketing_ticket_tasks.sql.
// completedAt/completedBy are stamped server-side when `done` flips.
export interface TicketTask {
  id: string
  orgId: string | null
  ticketId: string
  title: string
  assigneeId: string | null
  dueDate: string | null // YYYY-MM-DD
  done: boolean
  completedAt: string | null
  completedBy: string | null
  seq: number
  createdBy: string | null
  createdAt: string
}

export interface TicketTaskInput {
  title: string
  assigneeId?: string | null
  dueDate?: string | null
  done?: boolean
  seq?: number
}

// Ticket number format per customer (it_ticket_number_settings, 00547/00580):
// the next ticket is `${prefix}${String(nextNumber).padStart(padding, '0')}`.
export interface TicketNumberSettings {
  orgId: string | null
  prefix: string
  padding: number
  nextNumber: number
}

/** The ticket number the given settings will produce next, e.g. "GTX-00042". */
export function formatTicketNumber(s: Pick<TicketNumberSettings, 'prefix' | 'padding'>, n: number): string {
  return `${s.prefix}${String(n).padStart(s.padding, '0')}`
}

/** Validation for the three fields; empty object = valid. Mirrors the 00580 constraints. */
export function validateNumbering(v: { prefix: string; padding: string; nextNumber: string }): Partial<Record<'prefix' | 'padding' | 'nextNumber', string>> {
  const errors: Partial<Record<'prefix' | 'padding' | 'nextNumber', string>> = {}
  if (!v.prefix || v.prefix.length > 20) errors.prefix = '1 to 20 characters.'
  else if (/\s/.test(v.prefix)) errors.prefix = 'No spaces.'
  const pad = Number(v.padding)
  if (!Number.isInteger(pad) || pad < 1 || pad > 10) errors.padding = 'A whole number from 1 to 10.'
  const next = Number(v.nextNumber)
  if (!Number.isInteger(next) || next < 1) errors.nextNumber = 'A whole number, 1 or more.'
  return errors
}

// A user tied to a customer (it_ticketing_customer_members, 00586). A customer
// user sees their customer's whole ticket queue and nothing from any other
// customer; RevTech staff (admins, agents) are not limited by membership.
export interface TicketingCustomerMember {
  id: string
  customerId: string
  userId: string
  active: boolean
}
